import React, { useContext, useState } from "react";

import {
  Alert,
  Box,
  CircularProgress,
  DialogContent,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { SortDirection } from "@safetyculture/s12-apis-connect-web/s12/common/sorting_pb";
import { DocumentSortType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import {
  GetUserTemplatesAndInspectionsListParams,
  listUserInspections,
  listUserTemplates,
  transferUserInspections,
  transferUserTemplates,
} from "api/endpoints/templates-inspections";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import {
  UserInspectionListContext,
  UserTemplateListContext,
} from "context/user-templates-and-inspections-context";
import { toUUID } from "helpers/s12-helpers";
import { timeDelay } from "helpers/time";
import { UserUrlParamTypes } from "types/user-display";

interface TransferDocumentsDialogProps {
  open: boolean;
  setOpen: (newOpen: boolean) => void;
  isInspection: boolean;
  searchArchived: boolean;
  getUserItemsList: (userId: string, pageSize: number, offset: number) => void;
  orgId: string;
}
const BulkTransferDocumentsDialog = ({
  open,
  setOpen,
  isInspection,
  searchArchived,
  getUserItemsList,
  orgId,
}: TransferDocumentsDialogProps) => {
  const { userInspectionsList } = useContext(UserInspectionListContext);
  const { userTemplatesList } = useContext(UserTemplateListContext);
  //   userId here is the initial document owner id
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const [transferRecipientUserId, setTransferRecipientUserId] = useState("");
  const [isFetchingDocumentList, setIsFetchingDocumentList] = useState(false);
  const [isTransferInProgress, setIsTransferInProgress] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [numFailedTransferDocumentCalls, setNumFailedTransferDocumentCalls] =
    useState(0);
  const [successTransferDocumentsOpen, setSuccessTransferDocumentsOpen] =
    useState(false);
  const [failedTransferDocumentsOpen, setFailedTransferDocumentsOpen] =
    useState(false);

  const isInvalidInput =
    transferRecipientUserId !== "" &&
    !transferRecipientUserId.startsWith("user_");

  const totalItems = isInspection
    ? userInspectionsList.userInspectionsListResponse.totalItems
    : userTemplatesList.userTemplatesListResponse.totalItems;

  const getDocumentIdsForBulk = async (
    ownerId: string,
    pageSize: number,
    offset: number
  ) => {
    const ownerUUID = toUUID(ownerId);
    const payload: GetUserTemplatesAndInspectionsListParams = {
      ownerId: ownerUUID,
      pageSize: pageSize,
      offset: offset,
      sortDirection: SortDirection.DESC,
      sortType: DocumentSortType.DATE_CREATED,
      noPermissions: true,
      trashed: searchArchived,
      orgId: toUUID(orgId),
    };

    if (isInspection) {
      const inspectionIds = await listUserInspections(
        payload,
        callOptionsHeaders(regionCode, userId)
      ).then(async (res) => {
        return res.inspections.map((e) => e.id);
      });

      return inspectionIds;
    } else {
      const templateIds = await listUserTemplates(
        payload,
        callOptionsHeaders(regionCode, userId)
      ).then(async (res) => {
        return res.templates.map((e) => e.id);
      });
      return templateIds;
    }
  };

  const bulkTransfer = async (tranferRecipientUserID: string) => {
    setIsFetchingDocumentList(true);
    // batchIndex is used to get the offset to build out the total document id list
    let batchIndex = 0;
    const batchSize = 25;
    const batchSizeForFetching = 50;
    let numFailedTransfers = 0;
    const total = isInspection
      ? userInspectionsList.userInspectionsListResponse.totalItems
      : userTemplatesList.userTemplatesListResponse.totalItems;
    const documentIds: string[] = [];

    // fetch all of the document ids and put into a list
    for (let i = 0; i <= total / batchSizeForFetching; i++) {
      try {
        const documentIdBatch = await getDocumentIdsForBulk(
          userId,
          batchSizeForFetching,
          batchIndex
        );
        documentIds.push(...documentIdBatch);
        batchIndex += documentIdBatch.length;
      } catch {
        numFailedTransfers += batchSizeForFetching;
        batchIndex += batchSizeForFetching;
      }

      // Slight delay to ease load
      await timeDelay(100);
    }
    setIsFetchingDocumentList(false);

    let count = 0;
    const length = total / batchSize;
    // Default is 0 to be at start of list. Only gets updated if a batch failed to transfer.
    let transferFromDocListOffset = 0;
    setIsTransferInProgress(true);
    // Transfer all of the documents by id in batches of 25 from the fetched list
    for (let i = 0; i < length && total > 0; i++) {
      // Using splice to mutate the documentIds array, if the transfer batch didn't succeed, we don't
      // want to keep retrying the same batch.
      const transferBatchSlice = documentIds.splice(
        transferFromDocListOffset,
        batchSize
      );
      if (isInspection) {
        try {
          await transferUserInspections(
            {
              fromId: toUUID(userId),
              toId: toUUID(tranferRecipientUserID),
              inspectionIds: transferBatchSlice,
            },
            callOptionsHeaders(regionCode, userId)
          );
        } catch {
          numFailedTransfers += transferBatchSlice.length;
          transferFromDocListOffset += transferBatchSlice.length;
        }
      } else if (documentIds) {
        try {
          await transferUserTemplates(
            {
              fromId: toUUID(userId),
              toId: toUUID(tranferRecipientUserID),
              templateIds: transferBatchSlice,
            },
            callOptionsHeaders(regionCode, userId)
          );
        } catch {
          numFailedTransfers += transferBatchSlice.length;
          transferFromDocListOffset += transferBatchSlice.length;
        }
      }
      count += 1;
      // Time delay is to provide rate limiting on document update calls
      await timeDelay(2000);
      setProgressPercentage((count / length) * 100);
    }

    setIsTransferInProgress(false);
    return numFailedTransfers;
  };

  const handleSubmit = async () => {
    const numFailedTransfers = await bulkTransfer(transferRecipientUserId);
    if (numFailedTransfers === 0) {
      setSuccessTransferDocumentsOpen(true);
    } else {
      setNumFailedTransferDocumentCalls(numFailedTransfers);
      setFailedTransferDocumentsOpen(true);
    }

    getUserItemsList(userId, 50, 0);
    setOpen(false);
    setTransferRecipientUserId("");
    setProgressPercentage(0);
  };

  return (
    <>
      <ConfirmDialog
        title={`Bulk Transfer User ${
          isInspection ? "Inspections" : "Templates"
        }`}
        open={open}
        setOpen={setOpen}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Transfer"
        onConfirm={handleSubmit}
        disabled={
          isInvalidInput ||
          transferRecipientUserId === "" ||
          isTransferInProgress ||
          isFetchingDocumentList
        }
        disableCancelBtn={isTransferInProgress || isFetchingDocumentList}
        disableBackgroundClick={true}
        disableCloseOnConfirm={true}
        onCancel={() => {
          setIsFetchingDocumentList(false);
          setIsTransferInProgress(false);
          setProgressPercentage(0);
          setTransferRecipientUserId("");
        }}
      >
        <DialogContent>
          <Alert
            severity="info"
            variant="outlined"
            style={{ marginBottom: "1rem" }}
          >
            <div>
              <div style={{ marginBottom: "8px" }}>
                Transferring {isInspection ? "inspections" : "templates"} will
                change their ownership to the user specified below. This feature
                only supports target users within the same data centre.{" "}
                {!isInspection &&
                  "It is recommended to use the private library or external sharing of templates to send a copy to a user in a different data centre."}
              </div>
              <div>
                <b>Important:</b> When{" "}
                {isInspection ? "inspections" : "templates"} are transferred to
                another user, all access rules are automatically reset to only
                include the new document owner. You will need to manually
                reconfigure sharing permissions and access rules after the
                transfer is complete.
              </div>
            </div>
          </Alert>
          <Alert
            severity="warning"
            variant="filled"
            style={{ marginBottom: "1rem" }}
          >
            Contact #business-systems slack channel if the{" "}
            {isInspection ? "inspection" : "template"}
            {totalItems !== 1 ? "s" : ""} contain
            <b> GRS {isInspection ? "and/or sites" : ""} </b> and will be
            transferred to a <b>different organisation</b>, because{" "}
            <b>they will be broken</b> after the transfer.
          </Alert>
          {isInspection && (
            <Alert
              severity="warning"
              variant="filled"
              style={{ marginBottom: "1rem" }}
            >
              Inspection history will <b>not be transferred</b> and will be{" "}
              <b>inaccessible after the migration</b>. Inform the users before
              proceeding.
            </Alert>
          )}
          <Typography marginTop="1rem">
            Enter the user ID of the recipient that <b>all {totalItems}</b>{" "}
            {isInspection ? "inspections" : "templates"} will be transferred to.
          </Typography>

          <TextField
            margin="normal"
            label="User ID"
            variant="outlined"
            size="small"
            style={{ width: "100%" }}
            value={transferRecipientUserId}
            onChange={(e) => setTransferRecipientUserId(e.target.value)}
            helperText={isInvalidInput ? "Invalid user ID" : ""}
            error={isInvalidInput}
            inputProps={{
              "data-testid": "transfer-document-to-user-field",
            }}
            disabled={isTransferInProgress || isFetchingDocumentList}
          />
          {isFetchingDocumentList && !isTransferInProgress && (
            <>
              <Grid
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "1.5rem",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                  }}
                >
                  <CircularProgress variant="indeterminate" size={50} />
                </Box>
              </Grid>
              <Typography
                variant="body2"
                marginTop="2rem"
                align="center"
                sx={{ fontStyle: "italic" }}
              >
                Fetching the list of{" "}
                {isInspection ? "inspections" : "templates"} to transfer, hold
                tight.
              </Typography>
            </>
          )}
          {isTransferInProgress && (
            <>
              <Grid
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "1.5rem",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                  }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={progressPercentage}
                    size={50}
                  />

                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                    >{`${Math.round(progressPercentage)}%`}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Typography
                variant="body2"
                marginTop="2rem"
                align="center"
                sx={{ fontStyle: "italic" }}
              >
                Do not close window while in progress.
              </Typography>
            </>
          )}
        </DialogContent>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={
          numFailedTransferDocumentCalls === 0 && successTransferDocumentsOpen
        }
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSuccessTransferDocumentsOpen(false)}
        data-testid="bulk-transfer-documents-success-toast"
      >
        <Alert severity="success" variant="filled">
          Successfully transferred all{" "}
          {isInspection ? "inspections" : "templates"}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={failedTransferDocumentsOpen}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setFailedTransferDocumentsOpen(false)}
        data-testid="bulk-transfer-documents-failed-toast"
      >
        <Alert severity="error" variant="filled">
          {numFailedTransferDocumentCalls}{" "}
          {isInspection ? "inspection" : "template"}
          {numFailedTransferDocumentCalls !== 1 ? "s" : ""} failed to be
          transferred
        </Alert>
      </Snackbar>
    </>
  );
};
export default BulkTransferDocumentsDialog;
